import { ActionContext, ActionTree } from 'vuex';
import { UnsavedChangesGuardState } from './types/unsavedChangesGuard.types';
import { RootState } from '@/store/store';

/**
 * Defines the actions for the UnsavedChangesGuard module.
 */
const actions: ActionTree<UnsavedChangesGuardState, RootState> = {
  /**
   * Action to be dispatched when a modal requiring confirmation is opened or minimized.
   * It commits the mutation to set the global flag to true.
   * @param {ActionContext<UnsavedChangesGuardState, RootState>} context - Vuex action context object. Provides access to commit, dispatch, state,
   * getters, rootState, rootGetters.
   */
  notifyModalOpened({
    commit,
  }: ActionContext<UnsavedChangesGuardState, RootState>): void {
    // Commit the mutation to update the state
    commit('SET_MODAL_ACTIVE', true);
  },

  /**
   * Action to be dispatched when a modal requiring confirmation is successfully closed.
   * It commits the mutation to set the global flag to false
   * @param {ActionContext<UnsavedChangesGuardState, RootState>} context - Vuex action context object. Provides access to commit, dispatch, state,
   */
  notifyModalClosed({
    commit,
  }: ActionContext<UnsavedChangesGuardState, RootState>): void {
    // Commit the mutation to update the state
    commit('SET_MODAL_ACTIVE', false);
  },

  /**
   * Action to be dispatched when the user selects "Keep Editing" in the unsaved changes dialog.
   * @param {ActionContext<UnsavedChangesGuardState, RootState>} context - Vuex action context.
   */
  notifyKeepEditingSelected({
    commit,
  }: ActionContext<UnsavedChangesGuardState, RootState>): void {
    commit('SET_USER_SELECTED_KEEP_EDITING', true);
  },

  /**
   * Action to be dispatched by a component after it has handled the "Keep Editing" signal (e.g., restored a minimized modal).
   * @param {ActionContext<UnsavedChangesGuardState, RootState>} context - Vuex action context.
   */
  resetKeepEditingFlag({
    commit,
  }: ActionContext<UnsavedChangesGuardState, RootState>): void {
    commit('SET_USER_SELECTED_KEEP_EDITING', false);
  },

  /**
   * Action to set the active state of the "Discard Unsaved Changes?" dialog.
   * @param {ActionContext<UnsavedChangesGuardState, RootState>} context - Vuex action context.
   * @param {boolean} isActive - True if the dialog is active, false otherwise.
   */
  setUnsavedChangesDialogActive(
    { commit }: ActionContext<UnsavedChangesGuardState, RootState>,
    isActive: boolean,
  ): void {
    commit('SET_UNSAVED_CHANGES_DIALOG_ACTIVE', isActive);
  },
};

export default actions;
